import { useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { openExternalUrl } from '@/lib/linking';
import { isWeb } from '@/lib/platform';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  ChevronDown,
  ChevronUp,
  Search,
  Mail,
  FileText,
  HelpCircle,
  Send,
  ExternalLink,
  BarChart3,
  FolderOpen,
  Users,
  Briefcase,
  Shield,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { AppLayout } from '@/components/AppLayout';
import { SectionDivider } from '@/components/SectionDivider';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { TextInput } from 'react-native';
import { PLAN_FAQS } from '@/lib/commerce/copy';

const FAQ_DATA = [
  {
    category: 'Dealflow & Scoring',
    icon: BarChart3,
    questions: [
      {
        q: 'How is the Investment Readiness Score calculated?',
        a: 'The score evaluates startups across 15 dimensions including team, traction, market, financials, defensibility, and more. Each dimension is scored by AI analysis of the startup\'s pitch deck and available data, weighted by stage-appropriate importance.',
      },
      {
        q: 'What do the score ranges mean?',
        a: '80-100: Highly investable — strong across most dimensions. 60-79: Developing — solid foundations with areas to strengthen. 40-59: Early stage — significant gaps to address before fundraising. Below 40: Not ready — fundamental elements missing.',
      },
      {
        q: 'How do I analyze a new pitch deck?',
        a: 'Go to Dealflow and click "Analyze Pitch Deck". Upload a PDF and our AI will extract key data, generate a startup summary, and produce a full score breakdown with evidence citations.',
      },
      {
        q: 'Can I compare multiple deals?',
        a: 'Yes. Select deals using the checkbox mode or click the comparison icon on individual cards. You can compare up to 4 deals side-by-side across all scoring dimensions.',
      },
    ],
  },
  {
    category: 'Due Diligence & Data Rooms',
    icon: FolderOpen,
    questions: [
      {
        q: 'How do I access a startup\'s data room?',
        a: 'From a startup\'s profile or dealflow card, check if data room access is available. If granted, you\'ll see their documents organized by category (financials, legal, product, team, etc.) in the Due Diligence view.',
      },
      {
        q: 'Can I leave comments on documents?',
        a: 'Yes. Open any document in the due diligence view and add comments. You can mark comments as private (visible only to you and co-investors) or flag documents for follow-up.',
      },
      {
        q: 'What is Shared Due Diligence?',
        a: 'Premium feature: invite other investors to collaborate on due diligence for a specific startup. Share document reviews, notes, and conclusions with your syndication partners.',
      },
    ],
  },
  {
    category: 'Portfolio & Commitments',
    icon: Briefcase,
    questions: [
      {
        q: 'How do I track my portfolio?',
        a: 'Go to Portfolio to add your investments. Track invested amounts, current valuations, ownership percentages, ROI, and IRR. You can view in list, grid, or compact layouts.',
      },
      {
        q: 'What\'s the difference between soft and hard commitments?',
        a: 'A soft commitment is a non-binding expression of interest with an indicative amount. A hard commitment is a binding commitment that can be converted to a final investment. You can upgrade soft to hard from the Due Diligence view.',
      },
      {
        q: 'Why are my financial values hidden?',
        a: 'For your privacy, financial values are masked by default on the dashboard, portfolio, and dealflow pages. Tap the eye icon to reveal them. Values are re-masked each time you navigate to the page.',
      },
    ],
  },
  {
    category: 'Account & Subscription',
    icon: Shield,
    questions: [
      {
        q: 'How is my data protected?',
        a: 'Inverio processes data under GDPR and Swiss nDSG regulations. Sensitive document analysis uses EU-sovereign AI providers. Your portfolio data and commitment details are never shared with startups or other investors.',
      },
      ...PLAN_FAQS.investor,
    ],
  },
];

export default function InvestorHelpScreen() {
  useTrackScreen('InvestorHelp');
  const colors = useThemeColors();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  const toggleQuestion = (questionId: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const filteredFAQ = searchQuery
    ? FAQ_DATA.map((category) => ({
        ...category,
        questions: category.questions.filter(
          (q) =>
            q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            q.a.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter((category) => category.questions.length > 0)
    : FAQ_DATA;

  return (
    <AppLayout title="Help & Support">
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        <KeyboardAwareBody className="flex-1" showsVerticalScrollIndicator={false}>
          <View className="px-5 pb-8 pt-4">

            {/* Search */}
            <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
              <View
                className="flex-row items-center rounded-xl px-4"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                }}
              >
                <Search size={20} color={colors.text.tertiary} />
                <TextInput
                  style={{ color: colors.text.primary }}
                  className="flex-1 py-3.5 pl-3 text-base"
                  placeholder="Search help topics..."
                  placeholderTextColor={colors.text.tertiary}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                />
              </View>
            </Animated.View>

            {/* Quick Actions */}
            <Animated.View entering={FadeInDown.delay(150).duration(500)} className="mb-6">
              <View className="flex-row gap-3">
                <AnimatedPressable
                  onPress={() => openExternalUrl('mailto:support@inverio.ai', 'InvestorHelp.email')}
                  className="flex-1 p-4 rounded-2xl items-center"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}
                >
                  <View
                    className="w-11 h-11 rounded-full items-center justify-center mb-2"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Mail size={22} color={colors.primary[600]} />
                  </View>
                  <Text style={{ color: colors.text.primary }} className="font-semibold text-sm">
                    Email Support
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5">
                    support@inverio.ai
                  </Text>
                </AnimatedPressable>

              </View>
            </Animated.View>

            <SectionDivider label="FAQ" marginTop={8} marginBottom={12} />

            {/* FAQ */}
            {filteredFAQ.map((category, catIndex) => {
              const Icon = category.icon;
              return (
                <Animated.View
                  key={category.category}
                  entering={FadeInDown.delay(200 + catIndex * 50).duration(400)}
                  className="mb-4"
                >
                  <View className="flex-row items-center gap-2 mb-2">
                    <Icon size={16} color={colors.primary[600]} />
                    <Text style={{ color: colors.text.primary }} className="font-semibold">
                      {category.category}
                    </Text>
                  </View>
                  <View
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                    }}
                  >
                    {category.questions.map((faq, qIndex) => {
                      const questionId = `${catIndex}-${qIndex}`;
                      const isExpanded = expandedQuestions.has(questionId);

                      return (
                        <View key={questionId}>
                          <AnimatedPressable
                            onPress={() => toggleQuestion(questionId)}
                            className="flex-row items-center px-4 py-3.5"
                            style={{
                              borderBottomWidth: qIndex < category.questions.length - 1 || isExpanded ? 1 : 0,
                              borderColor: colors.border.light,
                            }}
                          >
                            <Text style={{ color: colors.text.primary }} className="flex-1 font-medium text-sm">
                              {faq.q}
                            </Text>
                            {isExpanded ? (
                              <ChevronUp size={18} color={colors.text.tertiary} />
                            ) : (
                              <ChevronDown size={18} color={colors.text.tertiary} />
                            )}
                          </AnimatedPressable>
                          {isExpanded && (
                            <View
                              className="px-4 py-3"
                              style={{ backgroundColor: colors.surface.secondary }}
                            >
                              <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                                {faq.a}
                              </Text>
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </View>
                </Animated.View>
              );
            })}

            {filteredFAQ.length === 0 && (
              <View className="items-center py-8">
                <HelpCircle size={32} color={colors.text.tertiary} />
                <Text style={{ color: colors.text.tertiary }} className="mt-2">
                  No results for &quot;{searchQuery}&quot;
                </Text>
              </View>
            )}

            {/* Still need help */}
            <Animated.View entering={FadeInUp.delay(400).duration(500)} className="mt-4">
              <View
                className="p-5 rounded-2xl"
                style={{
                  backgroundColor: colors.primary[50],
                  borderWidth: 1,
                  borderColor: colors.primary[200],
                }}
              >
                <Text style={{ color: colors.primary[700] }} className="text-base font-semibold mb-1">
                  Still need help?
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-sm mb-4">
                  Our support team typically responds within 24 hours.
                </Text>
                <AnimatedPressable
                  onPress={() => openExternalUrl('mailto:support@inverio.ai?subject=Investor%20Support%20Request', 'InvestorHelp.email')}
                  className="flex-row items-center justify-center gap-2 py-3 rounded-xl"
                  style={{ backgroundColor: colors.primary[600] }}
                >
                  <Send size={16} color="white" />
                  <Text className="text-white font-semibold text-sm">Send a Message</Text>
                </AnimatedPressable>
              </View>
            </Animated.View>

          </View>
        </KeyboardAwareBody>
      </SafeAreaView>
    </AppLayout>
  );
}
